import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Moon, Sun, User, LogOut, Search, Heart, MessageSquare, Instagram, Facebook } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const cls = ({ isActive }) => `gg-nav-link ${isActive ? "gg-nav-link-active" : ""}`;

function LangSwitch({ suffix = "" }) {
  const { lang, setLang } = useApp();
  return (
    <div className="flex items-center border border-foreground h-9" data-testid={`lang-switch${suffix}`}>
      {["en", "de"].map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span className="border-l border-foreground h-full" />}
          <button data-testid={`lang-${l}${suffix}`} onClick={() => setLang(l)}
            className={`px-2.5 h-full font-display text-xs uppercase ${lang === l ? "bg-foreground text-background" : ""}`}>{l.toUpperCase()}</button>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Header() {
  const { user, theme, setTheme, t, logout } = useApp();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const focusSearch = () => {
    setOpen(false);
    nav("/");
    setTimeout(() => { const el = document.getElementById("gg-search-location"); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.focus(); }, 80);
  };
  const close = () => setOpen(false);
  const [pending, setPending] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin") { setPending(0); return; }
    const tick = () => api.get("/admin/stats").then(r => setPending(r.data.pending)).catch(() => {});
    tick(); const id = setInterval(tick, 60000); return () => clearInterval(id);
  }, [user?.role]);
  const adminLabel = `${t("admin")}${pending ? ` (${pending})` : ""}`;
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const tick = () => api.get("/messages/unread-count").then(r => setUnread(r.data.unread)).catch(() => {});
    tick(); const id = setInterval(tick, 60000); return () => clearInterval(id);
  }, [user?.user_id]); // eslint-disable-line
  const Badge = () => unread > 0 ? <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground font-display text-[10px] leading-none px-1 py-0.5 min-w-[16px] text-center" data-testid="unread-badge">{unread}</span> : null;

  return (
    <header className="sticky top-0 z-40 border-b border-foreground bg-background" data-testid="header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 h-14 md:h-16 gap-3">
        <Link to="/" className="shrink-0 flex items-center" data-testid="brand-logo">
          <img src="/brand/soul-radar-mark.png" alt="Soul Radar" className="h-[22px] w-auto md:hidden dark:[filter:invert(1)_hue-rotate(180deg)]" data-testid="brand-logo-mobile" />
          <img src="/brand/soul-radar-mark.png" alt="Soul Radar" className="hidden md:block h-[26px] w-auto dark:[filter:invert(1)_hue-rotate(180deg)]" data-testid="brand-logo-desktop" />
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-6" data-testid="primary-nav">
          <NavLink to="/" end className={cls} data-testid="nav-events">{t("events")}</NavLink>
          <NavLink to="/submit" className={cls} data-testid="nav-submit">{t("submitEvent")}</NavLink>
          {user && <NavLink to="/dashboard" className={cls} data-testid="nav-dashboard">{t("myEventsTitle")}</NavLink>}
          {user?.role === "admin" && <NavLink to="/admin" className={cls} data-testid="nav-admin">{adminLabel}</NavLink>}
        </nav>

        <div className="hidden md:flex items-center gap-1 ml-auto" data-testid="utility-nav">
          <button onClick={focusSearch} className="gg-icon-btn" aria-label={t("search")} data-testid="nav-search"><Search className="w-4 h-4" /></button>
          <Link to="/my-events" className="gg-icon-btn" aria-label={t("savedEvents")} data-testid="nav-my-events"><Heart className="w-4 h-4" /></Link>
          {user && <Link to="/messages" className="gg-icon-btn relative" aria-label={t("messages")} data-testid="nav-messages"><MessageSquare className="w-4 h-4" /><Badge /></Link>}
          {user ? (
            <Link to="/profile" data-testid="nav-profile" className="gg-nav-link"><User className="w-4 h-4" />{user.name.split(" ")[0]}</Link>
          ) : (
            <Link to="/auth" data-testid="nav-login" className="gg-nav-link"><User className="w-4 h-4" />{t("login")}</Link>
          )}
          <LangSwitch />
          <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="gg-icon-btn" aria-label="toggle theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user && <button data-testid="nav-logout" onClick={async () => { await logout(); nav("/"); }} className="gg-icon-btn" aria-label={t("logout")}><LogOut className="w-4 h-4" /></button>}
        </div>

        <div className="md:hidden flex items-center gap-1 ml-auto">
          <button onClick={focusSearch} className="gg-icon-btn" aria-label={t("search")} data-testid="nav-search-m"><Search className="w-5 h-5" /></button>
          <Link to="/my-events" className="gg-icon-btn" aria-label={t("myEvents")} data-testid="nav-my-events-m"><Heart className="w-5 h-5" /></Link>
          {user && <Link to="/messages" className="gg-icon-btn relative" aria-label={t("messages")} data-testid="nav-messages-m"><MessageSquare className="w-5 h-5" /><Badge /></Link>}
          <button className="gg-icon-btn" onClick={() => setOpen(v => !v)} data-testid="mobile-menu-toggle" aria-label={t("menu")}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-foreground bg-background" data-testid="mobile-menu">
          <nav className="flex flex-col divide-y divide-foreground">
            <Link to="/" onClick={close} className="gg-mobile-link" data-testid="m-nav-events">{t("events")}</Link>
            <Link to="/submit" onClick={close} className="gg-mobile-link" data-testid="m-nav-submit">{t("submitEvent")}</Link>
            <Link to="/my-events" onClick={close} className="gg-mobile-link" data-testid="m-nav-my-events">{t("savedEvents")}</Link>
            <Link to="/archive" onClick={close} className="gg-mobile-link" data-testid="m-nav-archive">{t("archive")}</Link>
            <Link to="/about" onClick={close} className="gg-mobile-link" data-testid="m-nav-about">{t("about")}</Link>
            <Link to="/faq" onClick={close} className="gg-mobile-link" data-testid="m-nav-faq">FAQ</Link>
            <Link to="/help" onClick={close} className="gg-mobile-link" data-testid="m-nav-help">{t("help")}</Link>
            <div className="flex items-center gap-3 px-4 py-3" data-testid="m-nav-social">
              <a href="https://www.instagram.com/soulradar.events/" target="_blank" rel="noreferrer noopener" className="gg-icon-btn" aria-label="Instagram" data-testid="m-social-instagram"><Instagram className="w-4 h-4" /></a>
              <a href="https://www.facebook.com/soulradar.events/" target="_blank" rel="noreferrer noopener" className="gg-icon-btn" aria-label="Facebook" data-testid="m-social-facebook"><Facebook className="w-4 h-4" /></a>
              <span className="eyebrow !text-[10px] text-muted-foreground">@soulradar.events</span>
            </div>
            <Link to="/guidelines" onClick={close} className="gg-mobile-link" data-testid="m-nav-guidelines">{t("guidelines")}</Link>
            {user && <Link to="/dashboard" onClick={close} className="gg-mobile-link" data-testid="m-nav-dashboard">{t("myEventsTitle")}</Link>}
            {user?.role === "admin" && <Link to="/admin" onClick={close} className="gg-mobile-link" data-testid="m-nav-admin">{adminLabel}</Link>}
            {user ? (
              <Link to="/profile" onClick={close} className="gg-mobile-link" data-testid="m-nav-profile">{t("profile")} · {user.name}</Link>
            ) : (
              <Link to="/auth" onClick={close} className="gg-mobile-link !bg-primary !text-primary-foreground" data-testid="m-nav-login">{t("login")}</Link>
            )}
          </nav>
          <div className="flex items-center justify-between gap-2 p-4 border-t border-foreground">
            <LangSwitch suffix="-m" />
            <div className="flex items-center gap-1">
              <button data-testid="theme-toggle-m" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="gg-icon-btn">{theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}</button>
              {user && <button data-testid="m-nav-logout" onClick={async () => { await logout(); close(); nav("/"); }} className="gg-icon-btn"><LogOut className="w-5 h-5" /></button>}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
